import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { BranchService } from '@core/services/branch.service';
import { LoadingService } from '@core/services/loading.service';
import { NavigationService } from '@core/services/navigation.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { WebSocketService } from '@core/services/websocket.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { LockService } from "@shared/lock.service";
import { OrganisationService } from '@core/services/organisation.service';
import { WorkSpaceService } from '@core/services/workspace.service';
import { LabelsService } from '@core/services/labels.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {
  loadingSubscription!: Subscription;
  loading:boolean = true;
  isLocked = false;
  lockMessage = '';
  loggedIn = false;
  showMenu = false;
  currentLang = '';


  constructor(
    private authService: AuthService,
    private loadingScreenService: LoadingService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private router:Router,
    private route: ActivatedRoute,
    private navigation:NavigationService,
    private branchService:BranchService,
    private webSocketService:WebSocketService,
    private organisationService:OrganisationService,
    private workSpaceService:WorkSpaceService,
    private lockService:LockService,
    private labelsService:LabelsService
  ) {

    this.translate.onLangChange.subscribe(value =>{
      if(this.loggedIn && this.currentLang != value.lang){
        this.currentLang = value.lang;
        this.labelsService.loadAndMerge(value.lang).pipe(take(1)).subscribe()
      }
    })

    
    this.translate.addLangs(["nl","en","fr"]);
    const lang = (localStorage.getItem('lang_sl')??'nl').toLowerCase();
    this.translate.use(lang);
    // Branch/vanity detection now runs as an APP_INITIALIZER (see app.module) so vanityMode is settled
    // before any guard or component reads it.
  }

  ngOnInit(): void {
    this.router.events.subscribe(route => {
      this.loadingScreenService.stopLoading();
      const htmlTag = document.querySelector('html');
      htmlTag?.classList.remove('dialogForm-locked');
      htmlTag?.classList.remove('manualMenu-locked');
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.showMenu = this.loggedIn && !this.routeHidesMenu();
    });

    this.route.queryParamMap.pipe(
      map((params: ParamMap) => params.get('l')),
    ).subscribe(lang =>{
       if(lang){
        this.translate.use(lang.toLowerCase());
       }
    });

    this.branchService.getBranch().subscribe(()=>{
      this.loading = false;
    })

    this.lockService.isLocked$.subscribe(
      (status: boolean) => {
        this.isLocked = status;
      }
    );

    this.lockService.message$.subscribe(
      (message: string) => {
        this.lockMessage = message;
      }
    );

    this.authService.isAuthenticated().subscribe(value => {
      this.loggedIn = value;
      this.showMenu = value && !this.routeHidesMenu();
      if(value){
        this.workSpaceService.init();
        this.organisationService.getAll();
        this.currentLang = '';
        const currentLang = localStorage.getItem('lang_sl');
        this.translate.use(currentLang??'nl');
        if(currentLang && this.authService.User.language != currentLang){
          this.authService.updateLanguage(this.authService.User.profileId, currentLang).subscribe();
        }

      }else{
        this.organisationService.clearOrgList();
      }
    });

  }



  private routeHidesMenu(): boolean {
    let route = this.route.snapshot;
    while (route.firstChild) route = route.firstChild;
    return route.data?.['hideMenu'] === true;
  }

  ngAfterViewInit(): void {

    this.loadingSubscription = this.loadingScreenService.loading$.pipe().subscribe(
      (status: boolean) => {
        if(status){
          document.getElementsByTagName('html')[0].classList.add("loading")
        }else{
          document.getElementsByTagName('html')[0].classList.remove("loading")
        }
      }
    );
  }
  ngOnDestroy() {
   this.loadingSubscription.unsubscribe();
  }
}
